import { TFunction } from 'i18next';
import * as Yup from 'yup';
import * as Utils from 'features/weatherStations/utils';

export const ATMOSPHERIC_FIELD_NAMES = [
  'air_temp',
  'dew_point_temperature',
  'relative_humidity',
  'wind_gust',
  'wind_speed',
  'precipitation_1hr',
];

export const OPERATORS = ['gte', 'lte'] as const;

export type FieldName = (typeof ATMOSPHERIC_FIELD_NAMES)[number];

export type Operator = (typeof OPERATORS)[number];

type Validation = {
  max: number;
  maxMsg: string;
  min: number;
  minMsg: string;
};

const TEMPERATURE_VALIDATION: Validation = {
  max: 150,
  maxMsg: 'weatherStations.alerts.inputs.thresholdValue.tempTooHigh',
  min: -100,
  minMsg: 'weatherStations.alerts.inputs.thresholdValue.tempTooLow',
};

const WIND_VALIDATION: Validation = {
  max: 200,
  maxMsg: 'weatherStations.alerts.inputs.thresholdValue.windTooHigh',
  min: 0.1,
  minMsg: 'weatherStations.alerts.inputs.thresholdValue.windTooLow',
};

const PRECIPITATION_VALIDATION: Validation = {
  max: 20,
  maxMsg: 'weatherStations.alerts.inputs.thresholdValue.precipitationTooHigh',
  min: 0.1,
  minMsg: 'weatherStations.alerts.inputs.thresholdValue.precipitationTooLow',
};

const VALIDATIONS: Record<FieldName, Validation> = {
  air_temp: TEMPERATURE_VALIDATION,
  dew_point_temperature: TEMPERATURE_VALIDATION,
  precipitation_1hr: PRECIPITATION_VALIDATION,
  relative_humidity: {
    max: 99.9,
    maxMsg: 'weatherStations.alerts.inputs.thresholdValue.humidityTooHigh',
    min: 0.1,
    minMsg: 'weatherStations.alerts.inputs.thresholdValue.humidityTooLow',
  },
  wind_gust: WIND_VALIDATION,
  wind_speed: WIND_VALIDATION,
};

export const getValidationSchema = (t: TFunction): Yup.SchemaOf<object> =>
  Yup.object().shape({
    fieldName: Yup.string()
      .required(t('weatherStations.alerts.inputs.fieldName.required'))
      .oneOf(
        ATMOSPHERIC_FIELD_NAMES,
        'weatherStations.alerts.inputs.fieldName.invalid',
      ),
    operator: Yup.string()
      .required(t('weatherStations.alerts.inputs.operator.required'))
      .oneOf([...OPERATORS], 'weatherStations.alerts.inputs.operator.invalid'),
    thresholdValue: Yup.number().when(
      'fieldName',
      (fieldName: FieldName, schema) =>
        schema
          .typeError(t('weatherStations.alerts.inputs.thresholdValue.invalid'))
          .required(t('weatherStations.alerts.inputs.thresholdValue.required'))
          .min(VALIDATIONS[fieldName].min, t(VALIDATIONS[fieldName].minMsg))
          .max(VALIDATIONS[fieldName].max, t(VALIDATIONS[fieldName].maxMsg)),
    ),
  });

export const getFieldNameMenuItems = (
  t: TFunction,
  isPrecipitationEnabled: boolean,
): { [key: string]: string } => {
  const items: { [key: string]: string } = {
    air_temp: t('weatherStations.alerts.inputs.fieldName.options.air_temp'),
    dew_point_temperature: t(
      'weatherStations.alerts.inputs.fieldName.options.dew_point_temperature',
    ),
    relative_humidity: t(
      'weatherStations.alerts.inputs.fieldName.options.relative_humidity',
    ),
    wind_speed: t('weatherStations.alerts.inputs.fieldName.options.wind_speed'),
    wind_gust: t('weatherStations.alerts.inputs.fieldName.options.wind_gust'),
  };

  if (isPrecipitationEnabled) {
    items.precipitation_1hr = t(
      'weatherStations.alerts.inputs.fieldName.options.precipitation',
    );
  }

  return items;
};

export const getOperatorMenuItems = (
  t: TFunction,
): { [key: string]: string } => ({
  gte: t('weatherStations.alerts.inputs.operator.options.gte'),
  lte: t('weatherStations.alerts.inputs.operator.options.lte'),
});

export const mapFieldNameToAdornment: Record<FieldName, string> = {
  air_temp: '˚F',
  dew_point_temperature: '˚F',
  precipitation_1hr: 'in/hr',
  relative_humidity: '%',
  wind_speed: 'mph',
  wind_gust: 'mph',
};

export const getThresholdValue = (
  value: string | number | undefined,
  fieldName: string,
): number => {
  const thresholdValue =
    typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (fieldName === 'air_temp' || fieldName === 'dew_point_temperature') {
    return Utils.convertFahrenheitToCelsius(thresholdValue);
  }
  if (fieldName === 'wind_gust' || fieldName === 'wind_speed') {
    return Utils.convertMphToMetersPerSec(thresholdValue);
  }
  return thresholdValue;
};

export const convertExistingThresholdValue = (
  value: number | string | undefined,
  fieldName: string,
): string => {
  if (value === '' || value == null) return '';

  const thresholdValue = typeof value === 'string' ? parseFloat(value) : value;
  if (fieldName === 'air_temp' || fieldName === 'dew_point_temperature') {
    return Utils.convertCelsiusToFahrenheit(thresholdValue);
  }
  if (fieldName === 'wind_gust' || fieldName === 'wind_speed') {
    return Utils.convertMetersPerSecToMph(thresholdValue);
  }
  return String(thresholdValue);
};
